import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';
import { CulturalEvent } from '../constants/culturalEventsTypes';

// Force production API URL (override any env vars)
const API_BASE_URL = 'https://cultural-events-api-80b5fb86b6c5.herokuapp.com/api';

export const useAdminAPI = () => {
  const { getAuthHeader } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAllEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔐 Fetching all events (admin)');
      const response = await axios.get(`${API_BASE_URL}/admin/cultural-events`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      console.log('✅ Admin events fetched:', response.data);
      return response.data.events || response.data;
    } catch (err: any) {
      console.error('❌ Error fetching admin events:', err);
      setError(err.response?.data?.message || 'Failed to fetch events');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const uploadPDF = useCallback(async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      console.log('📤 Uploading PDF:', file.name);
      
      const formData = new FormData();
      formData.append('pdf', file);

      // Set timeout to 5 minutes for AI processing
      const response = await axios.post(
        `${API_BASE_URL}/admin/cultural-events/upload`,
        formData,
        {
          headers: {
            'Authorization': getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 5 * 60 * 1000, // 5 minutes timeout
          onUploadProgress: (progressEvent) => {
            // This will be called during file upload (before AI processing)
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`📤 Upload progress: ${percentCompleted}%`);
            }
          }
        }
      );
      
      console.log('✅ PDF uploaded successfully:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error uploading PDF:', err);
      
      // Improved error handling
      let errorMessage = 'Failed to upload PDF';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'Upload timeout: The AI extraction is taking longer than expected. Please try again.';
      } else if (err.response?.data) {
        errorMessage = err.response.data.message || err.response.data.error || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const updateEvent = useCallback(async (id: string, data: Partial<CulturalEvent>) => {
    try {
      setLoading(true);
      setError(null);
      console.log('✏️ Updating event:', id);
      const response = await axios.put(
        `${API_BASE_URL}/admin/cultural-events/${id}`,
        data,
        {
          headers: { 'Authorization': getAuthHeader() }
        }
      );
      console.log('✅ Event updated:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error updating event:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      
      // Improved error handling
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to update event';
      
      const errorDetails = err.response?.data?.details;
      const fullError = errorDetails 
        ? `${errorMessage}\n\nDetails: ${Array.isArray(errorDetails) ? errorDetails.map((d: any) => `\n- ${d.field || ''}: ${d.message || d}`).join('') : errorDetails}`
        : errorMessage;
      
      setError(fullError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const publishEvent = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🚀 Publishing event:', id);
      const response = await axios.patch(
        `${API_BASE_URL}/admin/cultural-events/${id}/publish`,
        {},
        {
          headers: { 'Authorization': getAuthHeader() }
        }
      );
      console.log('✅ Event published:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error publishing event:', err);
      setError(err.response?.data?.message || 'Failed to publish event');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const unpublishEvent = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔒 Unpublishing event:', id);
      
      // First fetch the event to get all current data
      const eventResponse = await axios.get(
        `${API_BASE_URL}/admin/cultural-events/${id}`,
        {
          headers: { 'Authorization': getAuthHeader() }
        }
      );
      
      const currentEvent = eventResponse.data.event || eventResponse.data;
      
      // Update with all current data plus status change
      const response = await axios.put(
        `${API_BASE_URL}/admin/cultural-events/${id}`,
        {
          ...currentEvent,
          status: 'draft'
        },
        {
          headers: { 
            'Authorization': getAuthHeader(),
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ Event unpublished:', response.data);
      return response.data;
    } catch (err: any) {
      console.error('❌ Error unpublishing event:', err);
      console.error('❌ Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url
      });
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to unpublish event';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const deleteEvent = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🗑️ Deleting event:', id);
      await axios.delete(
        `${API_BASE_URL}/admin/cultural-events/${id}`,
        {
          headers: { 'Authorization': getAuthHeader() }
        }
      );
      console.log('✅ Event deleted');
    } catch (err: any) {
      console.error('❌ Error deleting event:', err);
      setError(err.response?.data?.message || 'Failed to delete event');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  return {
    loading,
    error,
    getAllEvents,
    uploadPDF,
    updateEvent,
    publishEvent,
    unpublishEvent,
    deleteEvent
  };
};

